import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '~/app/utils/utils'

/**
 *
 * @returns: En knap-komponent med brugerdefineret styling
 * @example:
 * <AdvancedButton variant="default">Knap</AdvancedButton>
 * <AdvancedButton variant="default"><Link>Knap</Link></AdvancedButton>
 * <AdvancedButton variant="default"><Icon /><Link>Knap</Link></AdvancedButton>
 * @alias: AdvancedButton
 * @module: components/AdvancedButton
 * @summary: Denne komponent bruges til at oprette en ny knap med brugerdefinerede stilarter.
 * @see: src/components/AdvancedButton.tsx
 * @version: 1.0.0
 * @property: [variant, size, asChild]
 * @author: Kasper Buchholtz
 *
 **/

const buttonVariants = cva(
  'inline-flex items-center gap-4 justify-center rounded text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0689FB] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50', // whitespace-nowrap
  {
    variants: {
      variant: {
        default: 'bg-DabraRød text-lys hover:bg-[##ab4329]',
        primary: 'bg-DabraRød text-lys hover:bg-[##ab4329]',
        secondary: 'bg-DabraRød text-lys hover:bg-[##ab4329]',
        tertiary: 'bg-lys text-DabraRød hover:bg-[#E5E5E5]',
        radio:
          'bg-inherit border-1 rounded-lg text-rødtekst hover:bg-[#E5E5E5]',
        none: '',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        full: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface AdvancedButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  variant: 'default' | 'secondary' | 'primary' | 'none' | 'tertiary'
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'full'
  asChild?: boolean
}

const AdvancedButton = React.forwardRef<HTMLButtonElement, AdvancedButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
AdvancedButton.displayName = 'Button'

export { AdvancedButton, buttonVariants }
